import {
  Component,
  computed,
  effect,
  inject,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { UploadService } from '../../services/upload-service';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { FormatDurationPipe } from '@kinomap-dev/ng-ui-lib/core';
import { UploadFileProgressBlock } from '../../components/upload-file-progress-block/upload-file-progress-block';
import { MediaBlockService } from '../../services/media-block-service';
import { SyncroInformation } from '../../components/syncro-information/syncro-information';
import { ColorService } from '../../services/color.service';
import { ContentType } from '../content-creation/content-creation';
import { CreateContentService } from '../../services/create-content.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, Observable } from 'rxjs';
import { Toaster } from '../../services/toaster';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
  selector: 'app-upload-progression',
  imports: [
    TranslateModule,
    UpperFirstLetterPipe,
    IconComponent,
    FileSizePipe,
    FormatDurationPipe,
    UploadFileProgressBlock,
    SyncroInformation,
  ],
  templateUrl: './upload-progression.html',
  styleUrl: './upload-progression.scss',
})
export class UploadProgression {
  uploadService: UploadService = inject(UploadService);
  public mediaBlockService: MediaBlockService = inject(MediaBlockService);
  public createContentService: CreateContentService = inject(CreateContentService);
  protected readonly contentType: ContentType | undefined = this.createContentService.contentType();

  transferSpeed: Signal<number | undefined> = this.uploadService.transferSpeed;
  transferTimeEstimation: Signal<number | undefined> = computed(() =>
    this.uploadService.transferTimeEstimation()
      ? Math.round(this.uploadService.transferTimeEstimation()!)
      : undefined,
  );
  public colorService: ColorService = inject(ColorService);
  uploadComplete: OutputEmitterRef<void> = output();

  gpsFile: Signal<File | undefined> = this.mediaBlockService.gpsFile;
  gpsUploadUrl: Signal<string | undefined> = this.uploadService.s3GpsUploadUrl;

  videoFile: Signal<File | undefined> = this.mediaBlockService.videoFile;
  videoUploadUrl: Signal<string | undefined> = this.uploadService.s3VideoUploadUrl;

  gpsUploadComplete: WritableSignal<boolean> = signal(false);
  videoUploadComplete: WritableSignal<boolean> = signal(false);
  workoutUploadComplete: WritableSignal<boolean> = signal(false);

  private readonly router: Router = inject(Router);
  private readonly toaster: Toaster = inject(Toaster);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  protected readonly workoutUpload: Observable<number> | undefined =
    this.contentType === 'workout'
      ? this.uploadService.sendWorkout().pipe(
          catchError(() => {
            this.analyticsService.track('upload_failed', { content_type: 'workout', step: 'api' });
            this.toaster.error(
              this.translateService.instant('content_creation_preview.errors.title'),
              this.translateService.instant('content_creation_preview.errors.upload_failed'),
              'warning',
            );
            this.router.navigate(['content-creation']);
            return EMPTY;
          }),
        )
      : undefined;

  constructor() {
    effect(() => {
      if (!this.contentType) return;
      switch (this.contentType) {
        case 'gps-only':
          if (this.gpsUploadComplete()) {
            setTimeout(() => this.uploadComplete.emit(), 3000);
          }
          return;
        case 'workout':
          if (this.workoutUploadComplete()) {
            setTimeout(() => this.uploadComplete.emit(), 2000);
          }
          return;
        default:
          if (this.gpsUploadComplete() && this.videoUploadComplete()) {
            setTimeout(() => this.uploadComplete.emit(), 2000);
          }
          return;
      }
    });
  }
}
